// src/pages/ComplianceCalendar.jsx
// Phase E3 — Compliance Calendar. Auto-generated CIPC/tax/AFS/B-BBEE deadlines
// per client, plus custom items, with a firm-wide upcoming-deadlines view.
import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { Calendar, Pencil, Trash2, Archive } from 'lucide-react'
import { getClients, getComplianceItems, addComplianceItem, updateComplianceItem,
         deleteComplianceItem, generateComplianceItems, sendComplianceDigest } from '../api/kailux'
import Spinner from '../components/Spinner'

import Icon from '../components/Icon'

import { T } from '../theme/theme'
const ITEM_LABELS = {
  afs_due:                 'AFS Due',
  prov_tax_1:              'Provisional Tax (1st period)',
  prov_tax_2:              'Provisional Tax (2nd period)',
  cipc_annual_return:      'CIPC Annual Return',
  bbee_affidavit:          'B-BBEE Affidavit',
  itr14_income_tax_return: 'ITR14 Income Tax Return',
  custom:                  'Custom',
}

function urgency(daysUntil) {
  if (daysUntil < 0)   return { color: T.rd, bg: T.rdd, label: 'Overdue' }
  if (daysUntil <= 7)  return { color: T.rd, bg: T.rdd, label: 'Due this week' }
  if (daysUntil <= 30) return { color: T.or, bg: T.ord, label: 'Due this month' }
  return { color: T.gn, bg: T.gnd, label: 'On track' }
}

export default function ComplianceCalendar() {
  const { firm } = useAuth()
  const [clients, setClients] = useState([])
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(true)
  const [filterClient, setFilterClient] = useState('')
  const [showCompleted, setShowCompleted] = useState(false)

  const [showAdd, setShowAdd] = useState(false)
  const [newClientId, setNewClientId] = useState('')
  const [newDesc, setNewDesc]   = useState('')
  const [newDate, setNewDate]   = useState('')
  const [adding, setAdding]     = useState(false)

  const [editingId, setEditingId] = useState(null)
  const [editDesc, setEditDesc]   = useState('')
  const [editDate, setEditDate]   = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  const load = () => {
    if (!firm?.id) return
    Promise.all([
      getClients(firm.id),
      getComplianceItems({ firm_id: firm.id, include_completed: showCompleted }),
    ]).then(([c, i]) => {
      setClients(c.data || [])
      setItems((i.data || []).sort((a,b) => a.days_until - b.days_until))
    }).catch(console.error)
    .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [firm?.id, showCompleted])

  const markComplete = async (id) => {
    try {
      await updateComplianceItem(id, { status: 'completed' })
      load()
    } catch { alert('Could not update this item.') }
  }

  const remove = async (id) => {
    if (!confirm('Delete this compliance item? This cannot be undone.')) return
    try { await deleteComplianceItem(id); load() }
    catch { alert('Could not delete this item.') }
  }

  const startEdit = (item) => {
    setEditingId(item.id)
    setEditDesc(item.description || '')
    setEditDate(item.due_date ? item.due_date.slice(0, 10) : '')
  }

  const cancelEdit = () => {
    setEditingId(null); setEditDesc(''); setEditDate('')
  }

  const saveEdit = async (id) => {
    if (!editDate) return
    setSavingEdit(true)
    try {
      await updateComplianceItem(id, { description: editDesc.trim(), due_date: editDate })
      cancelEdit()
      load()
    } catch { alert('Could not save changes to this item.') }
    finally { setSavingEdit(false) }
  }

  // "Archive" is distinct from "Mark done": it hides an auto-generated item
  // that doesn't actually apply to this client (e.g. B-BBEE affidavit for a
  // client that isn't required to file one), without falsely recording it
  // as filed/completed the way "Mark done" would.
  const archive = async (id) => {
    if (!confirm('Archive this item? Use this for deadlines that don\'t actually apply to this client — it won\'t be marked as done, just hidden from the active list.')) return
    try { await updateComplianceItem(id, { status: 'dismissed' }); load() }
    catch { alert('Could not archive this item.') }
  }

  const generateForAll = async () => {
    for (const c of clients) {
      try { await generateComplianceItems(c.id) } catch { /* non-fatal */ }
    }
    load()
  }

  const addCustom = async () => {
    if (!newClientId || !newDesc.trim() || !newDate) return
    setAdding(true)
    try {
      await addComplianceItem({
        client_id: newClientId, firm_id: firm.id,
        item_type: 'custom', description: newDesc.trim(), due_date: newDate,
      })
      setNewClientId(''); setNewDesc(''); setNewDate(''); setShowAdd(false)
      load()
    } catch { alert('Could not add this item.') }
    finally { setAdding(false) }
  }

  const sendDigest = async () => {
    try {
      const res = await sendComplianceDigest(firm.id)
      if (res.data.status === 'no_upcoming_items') alert('No deadlines in the next 30 days — nothing to send.')
      else alert(`Digest email sent — ${res.data.count} upcoming deadline(s).`)
    } catch { alert('Could not send digest email.') }
  }

  const filtered = items.filter(i => !filterClient || i.client_id === filterClient)

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, padding:'32px' }}>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800' }}>
            Compliance Calendar
          </div>
          <div style={{ fontSize:'13px', fontWeight:'400', color:T.mt, marginTop:'4px' }}>
            CIPC, provisional tax, AFS, and B-BBEE deadlines — auto-generated from your client details
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={sendDigest}
            style={{ padding:'10px 18px', borderRadius:'8px', border:`1px solid ${T.bd}`,
              background:'transparent', color:T.lt, fontFamily:"'Outfit',sans-serif",
              fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
            <Icon name="mail" /> Email me a digest
          </button>
          <button onClick={generateForAll}
            style={{ padding:'10px 18px', borderRadius:'8px', border:`1px solid ${T.bd}`,
              background:'transparent', color:T.lt, fontFamily:"'Outfit',sans-serif",
              fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
            <Icon name="refresh" /> Refresh calendar
          </button>
          <button onClick={() => setShowAdd(s => !s)}
            style={{ padding:'10px 22px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
            {showAdd ? 'Cancel' : '+ Custom Item'}
          </button>
        </div>
      </div>

      {showAdd && (
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'20px', marginBottom:'20px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr 1fr auto', gap:'12px', alignItems:'flex-end' }}>
            <div>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Client</label>
              <select value={newClientId} onChange={e => setNewClientId(e.target.value)}
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }}>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Description</label>
              <input value={newDesc} onChange={e => setNewDesc(e.target.value)}
                placeholder="e.g. Industry license renewal"
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }} />
            </div>
            <div>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Due date</label>
              <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }} />
            </div>
            <button onClick={addCustom} disabled={adding}
              style={{ padding:'10px 22px', borderRadius:'8px', border:'none',
                background:T.prg, color:T.onpri,
                fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700',
                cursor: adding ? 'default' : 'pointer' }}>
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:'12px', marginBottom:'16px', alignItems:'center' }}>
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
          style={{ padding:'9px 14px', borderRadius:'8px', border:`1px solid ${T.bd}`,
            background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'13px' }}>
          <option value="">All clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'13px', color:T.lt, cursor:'pointer' }}>
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
          Show completed / archived
        </label>
      </div>

      <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:'32px', textAlign:'center' }}><Spinner fontSize='14px'/></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:'48px 32px', textAlign:'center' }}>
            <div style={{ marginBottom:'12px' }}><Calendar size={28} strokeWidth={1.5} color={T.mt}/></div>
            <div style={{ fontSize:'14px', fontWeight:'600', marginBottom:'6px' }}>No compliance items</div>
            <div style={{ fontSize:'13px', color:T.mt }}>
              Click "Refresh calendar" to auto-generate deadlines for all your clients
            </div>
          </div>
        ) : filtered.map((item, i) => {
          const u = urgency(item.days_until)
          const isCompleted = item.status === 'completed'
          const isArchived  = item.status === 'dismissed'
          const isDone = isCompleted || isArchived
          const isEditing = editingId === item.id
          const iconBtnStyle = { padding:'7px', borderRadius:'6px', border:`1px solid ${T.bd}`,
            background:'transparent', color:T.lt, cursor:'pointer', display:'flex', alignItems:'center' }
          return (
            <div key={item.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'14px 20px', borderBottom: i < filtered.length-1 ? `1px solid ${T.bd}` : 'none',
              opacity: isDone ? 0.5 : 1, gap:'16px' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:'14px', fontWeight:'700' }}>
                  {item.client_name}
                  <span style={{ color:T.mt, fontWeight:'500' }}> · {ITEM_LABELS[item.item_type] || item.item_type}</span>
                  {item.recurring && <span style={{ fontSize:'10px', color:T.mt, marginLeft:'8px' }}>↻ annual</span>}
                </div>
                {isEditing ? (
                  <div style={{ display:'flex', gap:'8px', marginTop:'6px', flexWrap:'wrap' }}>
                    <input value={editDesc} onChange={e => setEditDesc(e.target.value)}
                      placeholder="Description"
                      style={{ flex:'1 1 200px', padding:'7px 10px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'13px' }} />
                    <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
                      style={{ padding:'7px 10px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'13px' }} />
                    <button onClick={() => saveEdit(item.id)} disabled={savingEdit}
                      style={{ padding:'7px 14px', borderRadius:'6px', border:'none',
                        background:T.prg, color:T.onpri,
                        fontFamily:"'Outfit',sans-serif", fontSize:'12px', fontWeight:'700',
                        cursor: savingEdit ? 'default' : 'pointer' }}>
                      {savingEdit ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={cancelEdit}
                      style={{ padding:'7px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
                        fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                    {item.description}
                    {isArchived && <span style={{ color:T.mt, fontWeight:'700' }}> · Archived</span>}
                  </div>
                )}
              </div>
              {!isEditing && (
                <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:'14px', fontWeight:'700' }}>
                      {new Date(item.due_date).toLocaleDateString('en-ZA', { day:'numeric', month:'short', year:'numeric' })}
                    </div>
                    {!isDone && (
                      <span style={{ fontSize:'10px', fontWeight:'700', padding:'2px 8px', borderRadius:'6px',
                        color: u.color, background: u.bg }}>
                        {item.days_until < 0 ? `${Math.abs(item.days_until)}d overdue` : `${u.label} · ${item.days_until}d`}
                      </span>
                    )}
                  </div>
                  {!isDone && (
                    <button onClick={() => markComplete(item.id)}
                      style={{ padding:'7px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:'transparent', color:T.gn, fontFamily:"'Outfit',sans-serif",
                        fontSize:'12px', fontWeight:'600', cursor:'pointer', whiteSpace:'nowrap' }}>
                      <Icon name="check" /> Mark done
                    </button>
                  )}
                  <button onClick={() => startEdit(item)} title="Edit description or due date"
                    style={iconBtnStyle}>
                    <Pencil size={14} />
                  </button>
                  {!isDone && (
                    <button onClick={() => archive(item.id)} title="Archive — doesn't apply to this client"
                      style={iconBtnStyle}>
                      <Archive size={14} />
                    </button>
                  )}
                  <button onClick={() => remove(item.id)} title="Delete permanently"
                    style={{ ...iconBtnStyle, color:T.rd }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
